import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { outlookReturnMessage } from '@cerebro-diem/core';
import type { CalendarRole } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import {
  disconnectOutlook,
  importOutlookTasks,
  importSummary,
  outlookCalendars,
  outlookConnection,
  outlookKeys,
  startOutlookConnect,
  syncOutlook,
} from '../../services/outlook';
import { calendarKeys, setCalendarRole } from '../../services/calendar';
import { queryKeys } from '../../services/queries';
import { dangerButton, outlineButton, primaryButton } from '../../components/styles';
import CalendarRoleList from './CalendarRoleList';

/**
 * Connect the user's own Microsoft account (Outlook calendars and To Do),
 * give each calendar its role, sync, import To Do tasks, and disconnect.
 */
export default function OutlookSection() {
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  // What Microsoft's return said, read once; the address keeps no state worth sharing.
  const [returned] = useState(() => outlookReturnMessage(params.get('outlook')));
  const [importNote, setImportNote] = useState<string | null>(null);

  const connection = useQuery({ queryKey: outlookKeys.connection, queryFn: outlookConnection });
  const connected = connection.data ?? null;
  const calendars = useQuery({ queryKey: outlookKeys.calendars, queryFn: outlookCalendars, enabled: connected !== null });
  // Outlook's calendars feed every calendar view, so a change refreshes them all.
  const refresh = () => queryClient.invalidateQueries({ queryKey: calendarKeys.all });

  const connect = useMutation({ mutationFn: startOutlookConnect });
  const sync = useMutation({ mutationFn: syncOutlook, onSettled: refresh });
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: CalendarRole }) => setCalendarRole(id, role),
    // A calendar just turned on is synced straight away.
    onSuccess: async (_, { role }) => {
      await refresh();
      if (role !== 'off') sync.mutate();
    },
  });
  const importTasks = useMutation({
    mutationFn: importOutlookTasks,
    onSuccess: async (result) => {
      setImportNote(importSummary(result));
      await Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: queryKeys.inbox }), queryClient.invalidateQueries({ queryKey: ['tasks'] })]);
    },
  });
  const disconnect = useMutation({
    mutationFn: disconnectOutlook,
    onSuccess: () => {
      setImportNote(null);
      return refresh();
    },
  });

  const busy = connect.isPending || sync.isPending || importTasks.isPending || disconnect.isPending;
  const failure = [connect, sync, setRole, importTasks, disconnect].find((m) => m.isError)?.error;

  return (
    <section className="mb-8" aria-labelledby="outlook-heading">
      <h2 id="outlook-heading" className="text-lg font-semibold text-on-surface mb-4 flex items-center gap-2">
        <span className="text-xl">📅</span>
        Outlook
      </h2>
      <div className="bg-surface border border-outline/20 rounded-lg p-4 sm:p-6 space-y-4">
        {returned && (
          <p role="status" className={`text-sm ${returned.ok ? 'text-success' : 'text-error'}`}>
            {returned.message}
          </p>
        )}
        {connection.isError && <p className="text-sm text-error">Could not load Outlook: {errorMessage(connection.error)}</p>}
        {connection.isPending && <p className="text-sm text-on-surface-variant">Loading…</p>}

        {connection.isSuccess && connected === null && (
          <div className="space-y-3">
            <p className="text-on-surface-variant">
              Connect your own Microsoft account to see your Outlook calendars on Today and plan around busy time, and to
              bring your Microsoft To Do tasks into Cerebro. Cerebro only reads; it never changes your calendar or tasks.
            </p>
            <button type="button" onClick={() => connect.mutate()} disabled={busy} className={primaryButton}>
              Connect Outlook
            </button>
          </div>
        )}

        {connected && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-on-surface break-words">Connected as {connected.account_email ?? 'your Microsoft account'}</p>
                <p className="text-sm text-on-surface-variant">
                  {connected.last_sync_at ? `Last synced ${new Date(connected.last_sync_at).toLocaleString()}` : 'Not synced yet'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => sync.mutate()} disabled={busy || connected.needs_reconnect} className={outlineButton}>
                  {sync.isPending ? 'Syncing…' : 'Sync now'}
                </button>
                <button type="button" onClick={() => disconnect.mutate()} disabled={busy} className={dangerButton}>
                  Disconnect
                </button>
              </div>
            </div>

            {connected.needs_reconnect && (
              <div role="status" className="space-y-2">
                <p className="text-sm text-error">Microsoft no longer accepts the saved sign-in. Connect again to keep syncing.</p>
                <button type="button" onClick={() => connect.mutate()} disabled={busy} className={primaryButton}>
                  Connect again
                </button>
              </div>
            )}
            {!connected.needs_reconnect && connected.last_error && (
              <p className="text-sm text-error">The last sync failed: {connected.last_error}</p>
            )}

            <div>
              <h3 className="font-medium mb-1">Calendars</h3>
              <p className="text-sm text-on-surface-variant mb-3">
                Block: its events make that time busy. FYI: shown, never busy. Mixed: busy unless you mark an event not busy.
              </p>
              {calendars.isError && <p className="text-sm text-error">Could not load calendars: {errorMessage(calendars.error)}</p>}
              {calendars.data && calendars.data.length === 0 && (
                <p className="text-sm text-on-surface-variant">No calendars yet. Sync now to read them from Outlook.</p>
              )}
              <CalendarRoleList
                calendars={calendars.data ?? []}
                disabled={setRole.isPending}
                onChange={(id, role) => setRole.mutate({ id, role })}
              />
            </div>

            <div className="space-y-2">
              <h3 className="font-medium">Microsoft To Do</h3>
              <p className="text-sm text-on-surface-variant">
                Import your open To Do tasks with their notes, due dates, reminders and importance; completed ones are left behind. A task whose
                list has the name of one of your Projects or Areas goes there; the rest wait in your Inbox to file. Running
                it again only brings in tasks not imported before.
              </p>
              <button type="button" onClick={() => importTasks.mutate()} disabled={busy || connected.needs_reconnect} className={outlineButton}>
                {importTasks.isPending ? 'Importing…' : connected.tasks_imported_at ? 'Import new To Do tasks' : 'Import To Do tasks'}
              </button>
              {importNote && (
                <p role="status" className="text-sm text-success">
                  {importNote}
                </p>
              )}
              <p className="text-xs text-on-surface-variant">
                Outlook's sticky notes (classic Outlook Notes) cannot be read by any Microsoft API an app can use, so they are not imported.
              </p>
            </div>

            <p className="text-xs text-on-surface-variant">
              Disconnecting deletes the saved sign-in, your calendars and their events from Cerebro; imported tasks stay. To also
              withdraw Cerebro's access on Microsoft's side, remove it from your Microsoft account's app permissions.
            </p>
          </>
        )}

        {failure !== undefined && <p role="alert" className="text-sm text-error">{errorMessage(failure)}</p>}
      </div>
    </section>
  );
}
